// src/services/refreshManager.ts

import aplosApi from "@/services/aplosApi"; // Adjust the path as needed

export interface RefreshTask {
  /** A unique identifier for this task */
  id: string;
  /** EITHER the API endpoint to call (as a string) ... */
  endpoint?: string;
  /** ...OR a function that returns a Promise (e.g. one of your API service methods) */
  refreshFunction?: () => Promise<any>;
  /** How frequently (in milliseconds) this task should run */
  interval: number;
  /** Internal timer (setInterval) */
  timer?: ReturnType<typeof setInterval>;
  /** Optional callback to handle the returned data */
  callback?: (data: any) => void;
  /** Optional cooldown period (in milliseconds) */
  cooldown_timeout?: ReturnType<typeof setTimeout> | null;
  /* keep active internval until */
  active_interval_timeout?: number;
  /**
 * When true, skips the immediate runTask call on the first registration.
 * Automatically cleared after the first registration so that subsequent
 * re-registrations (e.g. from updateTaskInterval) still run immediately.
 */
  skipInitialRun?: boolean;
}

class RefreshManager {
  private tasks = new Map<string, RefreshTask>();

  /**
   * Registers a refresh task.
   *
   * @param task - The task object containing the id, endpoint/refreshFunction, interval, and callback.
   */
  public registerTask(task: RefreshTask): void {
    // If a task with this id already exists, remove it first.

    console.debug(`registering a task ${task.id}, ${task.interval}`)

    if (this.tasks.has(task.id)) {
      this.deregisterTask(task.id);
    }

    // Run the task immediately, unless the caller asked to skip the first run
    // (e.g. when the component that owns the data already loads it on mount).
    if (task.skipInitialRun) {
      task.skipInitialRun = false; // consume the flag so future re-registrations run normally
    } else {
      this.runTask(task);
    }

    // Now register it to run on its refresh rate.
    const timer = setInterval(async () => {
      await this.runTask(task);
    }, task.interval);

    task.timer = timer;
    this.tasks.set(task.id, task);
  }

  /**
   * Updates the refresh interval for an existing task.
   * The task’s timer is cleared and restarted with the new interval.
   *
   * @param id - The unique identifier of the task.
   * @param newInterval - The new refresh interval in milliseconds.
   */
  public updateTaskInterval(id: string, newInterval: number, active_interval_timeout?: number): void {
    const task = this.tasks.get(id);


    console.debug(`Possibly Updating task "${id}" to run at ${newInterval}ms, active_interval_timeout = ${active_interval_timeout}`)
    if (!task) {
      console.warn(`Task with id "${id}" not found.`);
      return;
    }

    // do we have an override to set or one in place
    if (active_interval_timeout) {
      const timeout = Date.now() + active_interval_timeout
      console.debug(`setting ${id} active_interval_timeout to ${timeout}ms`)
      task.active_interval_timeout = timeout;
    } else if (task.active_interval_timeout && task.active_interval_timeout > Date.now()) {

      const date_time = new Date(task.active_interval_timeout);
      const time = date_time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
      console.debug(`skipping Task "${id}" update, it's set to run active active until ${time}`);
      // one is already in place, so get out
      return;

    }

    // Only update if the interval has actually changed
    if (task.interval === newInterval) {
      console.debug(`Task "${id}" already running at ${newInterval}ms`);
      return;
    }

    console.debug(`Updating task "${id}" to run at ${newInterval}ms`)
    if (task.timer) {
      clearInterval(task.timer);
    }
    task.interval = newInterval;
    this.registerTask(task);
  }


  /**
   * Deregisters (removes) a refresh task and clears its timer.
   *
   * @param id - The unique identifier of the task to remove.
   */
  public deregisterTask(id: string): void {
    const task = this.tasks.get(id);
    if (task) {
      if (task.timer) {
        clearInterval(task.timer);
      }
      this.tasks.delete(id);
    } else {
      console.warn(`Task with id "${id}" not found.`);
    }
  }

  /**
   * Stops all tasks and clears all timers.
   */
  public stopAll(): void {
    for (const task of this.tasks.values()) {
      if (task.timer) {
        clearInterval(task.timer);
      }
    }
    this.tasks.clear();
  }



  private async runTask(task: RefreshTask): Promise<void> {
    try {
      let data: any;

      if (task.refreshFunction) {
        const response = await task.refreshFunction();
        data = response?.data;
      } else if (task.endpoint) {
        // Use your API service to call the endpoint (assumes GET request).
        const response = await aplosApi.apiInstance.get(task.endpoint);
        data = response.data;
      }
      // If neither refreshFunction nor endpoint is set, fall through to callback
      // (the callback itself is responsible for fetching, e.g. getExecutions).

      if (task.callback) {
        task.callback(data);
      } else if (task.refreshFunction || task.endpoint) {
        console.debug(`Task "${task.id}" refreshed with data:`, data);
      } else {
        console.error(
          `Refresh task "${task.id}" must have either a refreshFunction, an endpoint, or a callback.`
        );
      }
    } catch (error) {
      console.error(`Error refreshing task "${task.id}":`, error);
    }
  }

  /**
   * Triggers a refresh at the active interval for a specified duration,
   * then reverts the task to the idle interval.
   *
   * @param id - The ID of the task to trigger.
   * @param duration - The active period duration in milliseconds (e.g., 60000 for 60 seconds).
   * @param activeInterval - The active refresh interval in milliseconds.
   * @param idleInterval - The idle refresh interval in milliseconds.
   */
  public triggerActiveRefresh(
    id: string,
    duration: number,
    activeInterval: number,
    idleInterval: number
  ): void {
    const task = this.tasks.get(id);
    console.debug(`triggering an active refresh on: ${id} for : ${activeInterval}`)
    if (!task) {
      console.warn(`Task with id "${id}" not found.`);
      return;
    }
    console.debug(`triggering an active refresh on: ${id} for : ${activeInterval}`)
    // Immediately set the task to run at the active interval.
    this.updateTaskInterval(id, activeInterval);

    // After the specified duration, revert the task back to the idle interval.
    if (task.cooldown_timeout) {
      // if we already have one in-play, then we need to clear it
      console.debug(`clearing cooling down: ${task.id}`)
      clearTimeout(task.cooldown_timeout);
      task.cooldown_timeout = null;
    }
    console.debug(`Task "${id}" reverted to idle interval. in ${duration}`);
    task.cooldown_timeout = setTimeout(() => {
      this.updateTaskInterval(id, idleInterval);
      console.debug(`Task "${id}" reverted to idle interval.`);
    }, duration);
  }
}

const refreshManager = new RefreshManager();

// Expose the refresh manager to the window object for debugging purposes
if (typeof window !== 'undefined') {
  // @ts-ignore - Adding property to window for debugging
  window.refreshManager = refreshManager;
}

export default refreshManager;
